import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Mail } from "lucide-react";

const LOGO_URL = "/logo-infirmiers.png";

const Section = ({ title, children, id }) => (
  <section id={id} className="mb-10 scroll-mt-24">
    <h2 className="text-xl md:text-2xl font-semibold text-white mb-3">{title}</h2>
    <div className="text-[#94A3B8] text-sm md:text-base leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#0D1C32]">
      {/* Header */}
      <header className="border-b border-[#2A3A4D] sticky top-0 bg-[#0D1C32]/95 backdrop-blur-sm z-10">
        <div className="container-ip py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="back-home-link">
            <img src={LOGO_URL} alt="Infirmiers Privés" className="w-10 h-10 rounded-full object-cover" />
            <span className="font-semibold text-white">Infirmiers Privés</span>
          </Link>
          <Link 
            to="/" 
            className="inline-flex items-center text-sm text-[#94A3B8] hover:text-white"
            data-testid="back-home-text"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à l'accueil
          </Link>
        </div>
      </header>

      <main className="container-ip py-10 md:py-16 max-w-3xl">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 bg-[#00E073]/10 rounded-full flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-[#00E073]" />
          </div>
          <p className="text-xs uppercase tracking-wider text-[#28CAD4] font-semibold">Confidentialité &amp; RGPD</p>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
          Politique de confidentialité
        </h1>
        <p className="text-[#94A3B8] mb-10">
          Dernière mise à jour : 28 avril 2026
        </p>

        {/* Table of contents */}
        <nav className="bg-[#1F2A38] border border-[#2A3A4D] rounded-xl p-5 mb-12" aria-label="Sommaire">
          <p className="font-semibold text-white mb-3">Sommaire</p>
          <ol className="text-sm text-[#94A3B8] space-y-1.5 list-decimal list-inside">
            <li><a href="#identite" className="hover:text-[#00E073]">Qui sommes-nous ?</a></li>
            <li><a href="#donnees" className="hover:text-[#00E073]">Données collectées</a></li>
            <li><a href="#finalites" className="hover:text-[#00E073]">Finalités du traitement</a></li>
            <li><a href="#bases" className="hover:text-[#00E073]">Bases légales</a></li>
            <li><a href="#destinataires" className="hover:text-[#00E073]">Destinataires des données</a></li>
            <li><a href="#sous-traitants" className="hover:text-[#00E073]">Sous-traitants &amp; transferts hors UE</a></li>
            <li><a href="#duree" className="hover:text-[#00E073]">Durée de conservation</a></li>
            <li><a href="#droits" className="hover:text-[#00E073]">Vos droits</a></li>
            <li><a href="#securite" className="hover:text-[#00E073]">Sécurité des données</a></li>
            <li><a href="#cookies" className="hover:text-[#00E073]">Cookies</a></li>
            <li><a href="#contact" className="hover:text-[#00E073]">Contact &amp; réclamations</a></li>
          </ol>
        </nav>

        <Section id="identite" title="1. Qui sommes-nous ?">
          <p>
            <strong className="text-white">Infirmiers Privés</strong> est une plateforme d'intermédiation
            mettant en relation des patients en France avec des infirmiers libéraux diplômés d'État.
          </p>
          <p>
            Nous agissons exclusivement comme tiers facilitateur de mise en relation : nous ne réalisons
            aucun acte de soin et ne prodiguons aucun avis médical.
          </p>
          <p>
            Le responsable de traitement au sens du Règlement (UE) 2016/679 (« RGPD ») est&nbsp;:
            <br />
            <strong className="text-white">Infirmiers Privés</strong> — contact :{" "}
            <a href="mailto:contact@infirmiersprives.fr" className="text-[#00E073] hover:underline">
              contact@infirmiersprives.fr
            </a>
          </p>
        </Section>

        <Section id="donnees" title="2. Données collectées">
          <p>Nous collectons les catégories de données suivantes :</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>
              <strong className="text-white">Données d'identité &amp; de contact</strong> : nom, prénom,
              adresse email, numéro de téléphone, adresse postale.
            </li>
            <li>
              <strong className="text-white">Données de santé</strong> (uniquement quand vous les fournissez) :
              type de soin demandé, ordonnances ou certificats médicaux importés. Ces données relèvent
              de l'article 9 du RGPD et bénéficient d'une protection renforcée.
            </li>
            <li>
              <strong className="text-white">Données professionnelles</strong> (infirmiers) : numéro RPPS,
              attestation RCP, justificatif d'identité, IBAN (via Stripe Connect), informations entreprise.
            </li>
            <li>
              <strong className="text-white">Données de connexion</strong> : adresse IP, identifiants de
              session, journaux d'accès, type de navigateur.
            </li>
            <li>
              <strong className="text-white">Données de paiement</strong> : traitées exclusivement par
              notre prestataire Stripe — nous n'avons jamais accès à vos coordonnées bancaires.
            </li>
          </ul>
        </Section>

        <Section id="finalites" title="3. Finalités du traitement">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Création et gestion de votre compte.</li>
            <li>Mise en relation avec un infirmier libéral disponible dans votre zone.</li>
            <li>Transmission de l'ordonnance à l'infirmier choisi.</li>
            <li>Gestion des paiements et reversement des honoraires.</li>
            <li>Notifications par email (confirmation, rappel d'intervention, factures).</li>
            <li>Sécurisation de la plateforme et lutte contre la fraude.</li>
            <li>Respect de nos obligations légales (comptables, fiscales, sanitaires).</li>
          </ul>
        </Section>

        <Section id="bases" title="4. Bases légales">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>
              <strong className="text-white">Exécution du contrat</strong> : pour la mise en relation
              et la gestion du compte (article 6.1.b RGPD).
            </li>
            <li>
              <strong className="text-white">Consentement explicite</strong> : pour les données de santé
              (case à cocher RGPD lors de chaque demande de soin — article 9.2.a RGPD).
            </li>
            <li>
              <strong className="text-white">Obligation légale</strong> : pour la conservation des
              factures et la lutte contre la fraude.
            </li>
            <li>
              <strong className="text-white">Intérêt légitime</strong> : pour la sécurité de la
              plateforme et l'amélioration du service.
            </li>
          </ul>
        </Section>

        <Section id="destinataires" title="5. Destinataires des données">
          <p>Vos données sont accessibles uniquement aux personnes qui en ont besoin pour leur mission :</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>L'infirmier libéral que vous avez choisi (uniquement les informations utiles à la prise en charge).</li>
            <li>Notre équipe support, dans la stricte limite de ses missions.</li>
            <li>Nos sous-traitants (voir section 6).</li>
            <li>Les autorités administratives ou judiciaires en cas d'obligation légale.</li>
          </ul>
          <p>
            Vos données ne sont <strong className="text-white">jamais vendues ni cédées</strong> à des
            tiers à des fins commerciales.
          </p>
        </Section>

        <Section id="sous-traitants" title="6. Sous-traitants & transferts hors UE">
          <p>Nous faisons appel aux sous-traitants suivants :</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-[#2A3A4D] rounded-lg">
              <thead className="bg-[#1F2A38]">
                <tr>
                  <th className="text-left text-white px-3 py-2 border-b border-[#2A3A4D]">Prestataire</th>
                  <th className="text-left text-white px-3 py-2 border-b border-[#2A3A4D]">Finalité</th>
                  <th className="text-left text-white px-3 py-2 border-b border-[#2A3A4D]">Localisation</th>
                </tr>
              </thead>
              <tbody className="text-[#94A3B8]">
                <tr>
                  <td className="px-3 py-2 border-b border-[#2A3A4D]">Stripe</td>
                  <td className="px-3 py-2 border-b border-[#2A3A4D]">Paiements en ligne</td>
                  <td className="px-3 py-2 border-b border-[#2A3A4D]">Irlande / États-Unis (clauses contractuelles types)</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 border-b border-[#2A3A4D]">Resend</td>
                  <td className="px-3 py-2 border-b border-[#2A3A4D]">Envoi d'emails transactionnels</td>
                  <td className="px-3 py-2 border-b border-[#2A3A4D]">Union européenne</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 border-b border-[#2A3A4D]">OpenAI</td>
                  <td className="px-3 py-2 border-b border-[#2A3A4D]">
                    Analyse automatique des ordonnances (OCR/IA, à titre informatif uniquement)
                  </td>
                  <td className="px-3 py-2 border-b border-[#2A3A4D]">États-Unis (clauses contractuelles types)</td>
                </tr>
                <tr>
                  <td className="px-3 py-2">api-adresse.data.gouv.fr</td>
                  <td className="px-3 py-2">Géocodage des adresses</td>
                  <td className="px-3 py-2">France (service public)</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            Tous nos sous-traitants sont engagés contractuellement à respecter le RGPD et à mettre en
            œuvre les mesures de sécurité appropriées.
          </p>
        </Section>

        <Section id="duree" title="7. Durée de conservation">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Données de compte : pendant toute la durée d'utilisation, puis 3 ans à compter du dernier contact.</li>
            <li>Données de santé (ordonnances) : 5 ans après la dernière intervention (obligation légale).</li>
            <li>Factures : 10 ans (obligation comptable).</li>
            <li>Journaux de connexion : 1 an.</li>
            <li>Données fournies sans création de compte : supprimées sous 30 jours.</li>
          </ul>
        </Section>

        <Section id="droits" title="8. Vos droits">
          <p>
            Conformément au RGPD et à la loi « Informatique et Libertés » modifiée, vous disposez des
            droits suivants :
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong className="text-white">Droit d'accès</strong> à vos données personnelles.</li>
            <li><strong className="text-white">Droit de rectification</strong> en cas d'inexactitude.</li>
            <li><strong className="text-white">Droit à l'effacement</strong> (« droit à l'oubli »).</li>
            <li><strong className="text-white">Droit à la limitation</strong> du traitement.</li>
            <li><strong className="text-white">Droit à la portabilité</strong> de vos données.</li>
            <li><strong className="text-white">Droit d'opposition</strong> au traitement.</li>
            <li><strong className="text-white">Droit de retirer votre consentement</strong> à tout moment, sans effet rétroactif.</li>
            <li><strong className="text-white">Droit de définir des directives post-mortem</strong> sur le sort de vos données.</li>
          </ul>
          <p>
            Pour exercer ces droits, écrivez-nous à{" "}
            <a href="mailto:contact@infirmiersprives.fr" className="text-[#00E073] hover:underline">
              contact@infirmiersprives.fr
            </a>{" "}
            en joignant un justificatif d'identité. Nous vous répondrons sous 30 jours maximum.
          </p>
        </Section>

        <Section id="securite" title="9. Sécurité des données">
          <p>Nous mettons en œuvre les mesures techniques et organisationnelles suivantes :</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Chiffrement des communications (HTTPS/TLS).</li>
            <li>Hachage des mots de passe (bcrypt).</li>
            <li>Authentification par token sécurisé (JWT).</li>
            <li>Accès restreint aux données par habilitation interne.</li>
            <li>Sauvegardes régulières et journalisation des accès.</li>
            <li>Hébergement migré vers un Hébergeur de Données de Santé (HDS) certifié pour la mise en production.</li>
          </ul>
          <p>
            En cas de violation susceptible d'engendrer un risque pour vos droits et libertés, nous
            vous en informerons dans les meilleurs délais et au plus tard sous 72 heures comme prévu
            par le RGPD.
          </p>
        </Section>

        <Section id="cookies" title="10. Cookies">
          <p>
            Nous utilisons uniquement des cookies <strong className="text-white">strictement
            nécessaires</strong> au fonctionnement de la plateforme (session de connexion, préférences).
            Aucun cookie publicitaire ou tiers de mesure d'audience n'est déposé sans votre consentement.
          </p>
        </Section>

        <Section id="contact" title="11. Contact & réclamations">
          <p>
            Pour toute question concernant cette politique ou le traitement de vos données, contactez-nous :
          </p>
          <div className="bg-[#1F2A38] border border-[#2A3A4D] rounded-lg p-4 flex items-start gap-3">
            <Mail className="w-5 h-5 text-[#00E073] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white font-medium">contact@infirmiersprives.fr</p>
              <p className="text-xs text-[#64748B] mt-1">Réponse sous 30 jours maximum</p>
            </div>
          </div>
          <p className="mt-4">
            Si vous estimez, après nous avoir contactés, que vos droits ne sont pas respectés, vous
            pouvez introduire une réclamation auprès de la{" "}
            <a
              href="https://www.cnil.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#00E073] hover:underline"
            >
              CNIL (Commission Nationale de l'Informatique et des Libertés)
            </a>
            , 3 place de Fontenoy — TSA 80715 — 75334 Paris Cedex 07.
          </p>
        </Section>

        <div className="mt-16 pt-8 border-t border-[#2A3A4D]">
          <Link 
            to="/" 
            className="inline-flex items-center text-[#00E073] hover:text-[#00E073]/80 font-medium"
            data-testid="back-home-bottom"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à l'accueil
          </Link>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
